import { useEffect } from "react";
import { X } from "lucide-react";
import { formatKina, formatHours } from "@/lib/money";
import { LINE_DRAWER } from "@/constants/testIds";

function Row({ label, sub, value, mono = true, color, big }) {
    return (
        <div className="flex justify-between items-baseline py-1.5">
            <div>
                <div className="text-[13px] text-neutral-700">{label}</div>
                {sub && <div className="text-[10px] text-neutral-400 uppercase tracking-[0.14em] font-display mt-0.5">{sub}</div>}
            </div>
            <div className={`${mono ? "font-mono-money" : ""} ${big ? "text-[18px] font-semibold" : "text-[13px]"}`} style={{ color }}>
                {value}
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display mb-2 border-b border-neutral-200 pb-1.5">
                {title}
            </div>
            {children}
        </div>
    );
}

export default function PayLineDrawer({ open, line, onClose, onEditProfile }) {
    useEffect(() => {
        if (!open) return;
        const k = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", k);
        return () => window.removeEventListener("keydown", k);
    }, [open, onClose]);

    if (!open || !line) return null;

    const emp = line.employee || line.employee_snapshot || {};
    const c = line.calc || {};
    const h = line.hours || {};
    const rate = (line.rate_override != null ? line.rate_override : emp.labour_rate_kina) || 0;
    const deps = (line.dependants_override != null ? line.dependants_override : (emp.dependants_declared ?? emp.dependants)) || 0;

    return (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <aside data-testid={LINE_DRAWER.drawer} className="absolute right-0 top-0 h-full w-full max-w-[560px] bg-white shadow-2xl flex flex-col">
                {/* Dark head w/ red stripe */}
                <div className="px-6 pt-6 pb-4 text-white" style={{ background: "var(--kp-black)" }}>
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">
                                Employee detail
                            </div>
                            <div className="font-display font-bold text-2xl mt-1">{emp.name}</div>
                            <div className="text-xs text-neutral-400 mt-0.5">{emp.position || "—"}</div>
                        </div>
                        <button data-testid={LINE_DRAWER.closeBtn} onClick={onClose} className="p-1 text-neutral-400 hover:text-white">
                            <X size={18} />
                        </button>
                    </div>
                    <div className="h-[3px] mt-4" style={{ background: "var(--kp-red)" }} />
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-6">
                    <Section title="Wages buildup">
                        <Row label="Regular hours" sub={`${formatHours(h.regular)} × K${Number(rate).toFixed(2)}`} value={formatKina(c.wages_regular)} />
                        <Row label="Overtime × 1.5" sub={`${formatHours(h.time_and_half)} × K${(rate * 1.5).toFixed(2)}`} value={formatKina(c.wages_tah)} />
                        <Row label="Overtime × 2.0" sub={`${formatHours(h.double)} × K${(rate * 2).toFixed(2)}`} value={formatKina(c.wages_double)} />
                        <Row label="Sick (ord. rate)" sub={`${formatHours(h.sick)} × K${Number(rate).toFixed(2)}`} value={formatKina(c.wages_sick)} />
                        <Row label="Annual leave taken (ord. rate)" sub={`${formatHours(h.annual_leave_taken)} × K${Number(rate).toFixed(2)}`} value={formatKina(c.wages_annual_leave)} />
                        <Row label="Public holiday worked × 2.0" sub={`${formatHours(h.ph_worked ?? h.public_holiday)} × K${(Number(rate) * 2).toFixed(2)}`} value={formatKina(c.wages_ph_worked ?? c.wages_ph)} />
                        <Row label="Public holiday taken (ord. rate)" sub={`${formatHours(h.ph_taken)} × K${Number(rate).toFixed(2)}`} value={formatKina(c.wages_ph_taken)} />
                        <Row label="Travel allowance" value={formatKina(c.travel_allowance)} />
                        <Row label="Housing / other" value={formatKina(c.other_allowance)} />
                        <div className="border-t border-neutral-200 mt-2 pt-2">
                            <Row label="Taxable gross" value={formatKina(c.taxable_gross)} big />
                        </div>
                    </Section>

                    <Section title="Tax working · IRC Annexure B">
                        <Row label="Dependants" value={deps} mono={false} />
                        <Row label="Annualised (N)" sub="26 × fortnightly gross – deductions" value={formatKina(c.annualised_N)} />
                        <Row label="Annual gross tax" value={formatKina(c.annual_gross_tax)} />
                        <Row label="Dependants rebate" value={formatKina(c.dependants_rebate)} color="var(--kp-red)" />
                        <div className="border-t border-neutral-200 mt-2 pt-2">
                            <Row label="Annual net tax" value={formatKina(c.annual_net_tax)} />
                            <Row label="÷ 26 = fortnightly tax" value={formatKina(c.fortnightly_tax)} color="var(--kp-red)" big />
                        </div>
                    </Section>

                    <div className="rounded-[3px] p-4" style={{ background: "#F5F5F4" }}>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">
                            Method
                        </div>
                        <div className="text-[13px] text-neutral-700 mt-0.5">{c.method || "—"}</div>
                        <div className="flex items-center justify-between mt-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">Net pay</div>
                                <div data-testid={LINE_DRAWER.netAmount} className="font-display font-bold text-4xl mt-1 font-mono-money" style={{ color: "var(--kp-success)" }}>
                                    {formatKina(c.net_pay)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between">
                    <button onClick={onClose} className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600 hover:text-black">
                        Close
                    </button>
                    <button
                        data-testid={LINE_DRAWER.editProfileBtn}
                        onClick={() => onEditProfile?.(emp)}
                        className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px]"
                        style={{ background: "var(--kp-red)" }}
                    >
                        Edit employee profile →
                    </button>
                </div>
            </aside>
        </div>
    );
}
